import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { SeenLogo } from '@/components/common/SeenLogo';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Store, 
  MapPin, 
  ShoppingCart, 
  Search,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Crown,
  ShieldCheck,
  ShieldAlert,
  Package,
  LogIn,
  ArrowRight,
  Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

const StoreScanPage = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  
  const [store, setStore] = useState(null);
  const [safeProducts, setSafeProducts] = useState([]);
  const [warningProducts, setWarningProducts] = useState([]);
  const [userAllergens, setUserAllergens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showWarnings, setShowWarnings] = useState(false);
  const [stats, setStats] = useState({ safe: 0, warning: 0, total: 0 });

  const isPremium = ['pro', 'enterprise', 'festival', 'hardship'].includes(user?.subscription_plan);
  
  // Get brand colors with fallbacks
  const primaryColor = store?.primary_color || '#5DBDB3';
  const secondaryColor = store?.secondary_color || '#F08B7D';

  const fetchStoreInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/stores/${storeId}`);
      setStore(response.data);
    } catch (err) {
      setError('Store not found');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  const trackScan = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/api/stores/${storeId}/track-scan`, null, {
        params: {
          device_type: getDeviceType(),
          user_agent: navigator.userAgent
        },
        headers
      });
    } catch (err) {
      // Silent fail - don't block user experience for analytics
      console.log('Scan tracking failed:', err.message);
    }
  }, [storeId, token]);

  useEffect(() => {
    fetchStoreInfo();
    trackScan();
  }, [fetchStoreInfo, trackScan]);

  const fetchSafeProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/stores/${storeId}/safe-products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSafeProducts(response.data.safe_products || []);
      setWarningProducts(response.data.warning_products || []);
      setUserAllergens(response.data.user_allergens || []);
      setStats({
        safe: response.data.safe_count,
        warning: response.data.warning_count,
        total: response.data.total_in_stock
      });
    } catch (err) {
      if (err.response?.status === 403) {
        // Expected for non-premium users
      } else {
        toast.error('Failed to load products');
      }
    } finally {
      setLoading(false);
    }
  }, [storeId, token]);

  useEffect(() => {
    if (isAuthenticated && isPremium && store) {
      fetchSafeProducts();
    }
  }, [isAuthenticated, isPremium, store, fetchSafeProducts]);

  const filteredSafeProducts = safeProducts.filter(p => 
    p.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.brand && p.brand.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredWarningProducts = warningProducts.filter(p => 
    p.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.brand && p.brand.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading && !store) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#5DBDB3]/5 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#5DBDB3]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#5DBDB3]/5 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Store Not Found</h2>
            <p className="text-muted-foreground mb-4">This store may no longer exist or the QR code is invalid.</p>
            <Button onClick={() => navigate('/')} className="bg-[#5DBDB3] hover:bg-[#4AA89E]">
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#5DBDB3]/5 to-white" data-testid="store-scan-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <SeenLogo size="sm" />
          {!isAuthenticated && (
            <Button variant="outline" onClick={() => navigate('/auth')} size="sm" data-testid="sign-in-btn">
              <LogIn className="w-4 h-4 mr-2" />
              Sign In
            </Button>
          )}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Store Info Card with Custom Branding */}
        <Card className="mb-6 overflow-hidden" data-testid="store-info-card">
          <div 
            className="h-2" 
            style={{ background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }} 
          />
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              {store?.logo_url ? (
                <img 
                  src={store.logo_url} 
                  alt={store.name} 
                  className="w-14 h-14 rounded-xl object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
                style={{ 
                  backgroundColor: `${primaryColor}15`,
                  display: store?.logo_url ? 'none' : 'flex'
                }}
              >
                <Store className="w-7 h-7" style={{ color: primaryColor }} />
              </div>
              <div className="flex-1">
                <h1 className="text-xl font-bold" data-testid="store-name">{store?.name}</h1>
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" />
                  {store?.address}, {store?.city} {store?.postcode}
                </p>
                {store?.welcome_message && (
                  <p className="text-sm mt-2 p-2 rounded-lg" style={{ backgroundColor: `${primaryColor}10`, color: primaryColor }}>
                    {store.welcome_message}
                  </p>
                )}
                {store?.description && !store?.welcome_message && (
                  <p className="text-sm mt-2 text-muted-foreground">{store.description}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Not Authenticated State */}
        {!isAuthenticated && (
          <Card className="mb-6">
            <CardContent className="p-8 text-center">
              <ShieldCheck className="w-16 h-16 text-[#5DBDB3] mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Find Your Safe Products</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Sign in to see which products in this store are safe for you based on your allergen profile.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate('/auth')} className="bg-[#5DBDB3] hover:bg-[#4AA89E]">
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In to See Safe Products
                </Button>
                <Button variant="outline" onClick={() => navigate('/auth')}>
                  Create Free Account
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Authenticated but Not Premium */}
        {isAuthenticated && !isPremium && (
          <Card className="mb-6 border-amber-200 bg-amber-50/50">
            <CardContent className="p-8 text-center">
              <Crown className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Premium Feature</h2>
              <p className="text-muted-foreground mb-2">
                Store QR scanning is available for Premium members.
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Upgrade to Pro, Enterprise, Festival, or apply for <strong>free Hardship Access</strong> if you&apos;re on benefits.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate('/subscription')} className="bg-[#5DBDB3] hover:bg-[#4AA89E]">
                  <Crown className="w-4 h-4 mr-2" />
                  View Plans
                </Button>
                <Button variant="outline" onClick={() => navigate('/subscription')}>
                  Apply for Hardship Access
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Premium User - Show Products */}
        {isAuthenticated && isPremium && (
          <>
            {/* User Allergens Banner */}
            {userAllergens.length > 0 && (
              <Card className="mb-6 border-[#F08B7D]/30 bg-[#F08B7D]/5" data-testid="user-allergens-banner">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <AlertTriangle className="w-4 h-4 text-[#F08B7D]" />
                    <span className="text-sm font-medium">Your allergens:</span>
                    {userAllergens.map(allergen => (
                      <Badge key={allergen} variant="outline" className="bg-white border-[#F08B7D]/50 text-[#F08B7D]" data-testid={`allergen-badge-${allergen}`}>
                        {allergen}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6" data-testid="stats-grid">
              <Card data-testid="safe-count-card">
                <CardContent className="p-4 text-center">
                  <ShieldCheck className="w-6 h-6 text-green-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-green-600" data-testid="safe-count">{stats.safe}</p>
                  <p className="text-xs text-muted-foreground">Safe for you</p>
                </CardContent>
              </Card>
              <Card data-testid="warning-count-card">
                <CardContent className="p-4 text-center">
                  <ShieldAlert className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-amber-600" data-testid="warning-count">{stats.warning}</p>
                  <p className="text-xs text-muted-foreground">Contains allergens</p>
                </CardContent>
              </Card>
              <Card data-testid="total-count-card">
                <CardContent className="p-4 text-center">
                  <Package className="w-6 h-6 text-[#5DBDB3] mx-auto mb-1" />
                  <p className="text-2xl font-bold" data-testid="total-count">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total in stock</p>
                </CardContent>
              </Card>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="product-search-input"
              />
            </div>

            {/* Toggle */}
            <div className="flex gap-2 mb-4">
              <Button 
                variant={!showWarnings ? "default" : "outline"} 
                onClick={() => setShowWarnings(false)}
                className={!showWarnings ? "bg-green-600 hover:bg-green-700" : ""}
                data-testid="safe-products-toggle"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Safe Products ({filteredSafeProducts.length})
              </Button>
              <Button 
                variant={showWarnings ? "default" : "outline"} 
                onClick={() => setShowWarnings(true)}
                className={showWarnings ? "bg-amber-500 hover:bg-amber-600" : ""}
                data-testid="warning-products-toggle"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Contains Allergens ({filteredWarningProducts.length})
              </Button>
            </div>

            {/* Products List */}
            {loading ? (
              <div className="flex items-center justify-center py-12" data-testid="products-loading">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-3" data-testid="products-list">
                {!showWarnings && filteredSafeProducts.length === 0 && (
                  <Card data-testid="no-safe-products">
                    <CardContent className="p-8 text-center">
                      <ShieldCheck className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">No safe products found matching your search</p>
                    </CardContent>
                  </Card>
                )}

                {showWarnings && filteredWarningProducts.length === 0 && (
                  <Card data-testid="no-warning-products">
                    <CardContent className="p-8 text-center">
                      <ShieldAlert className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">No products with allergens found matching your search</p>
                    </CardContent>
                  </Card>
                )}

                {!showWarnings && filteredSafeProducts.map((product) => (
                  <Card key={product.id || product.product_barcode} className="border-green-200 hover:shadow-md transition-shadow" data-testid={`safe-product-${product.id || product.product_barcode}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          </div>
                          <div>
                            <p className="font-medium">{product.product_name}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              {product.brand && <span>{product.brand}</span>}
                              {product.aisle && (
                                <>
                                  <span>•</span>
                                  <span>{product.aisle}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        {product.price && (
                          <Badge variant="secondary" className="text-lg font-semibold">
                            £{product.price.toFixed(2)}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {showWarnings && filteredWarningProducts.map((product) => (
                  <Card key={product.id || product.product_barcode} className="border-amber-200 hover:shadow-md transition-shadow" data-testid={`warning-product-${product.id || product.product_barcode}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                          </div>
                          <div>
                            <p className="font-medium" data-testid={`warning-product-name-${product.id || product.product_barcode}`}>{product.product_name}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              {product.brand && <span>{product.brand}</span>}
                              {product.aisle && (
                                <>
                                  <span>•</span>
                                  <span>{product.aisle}</span>
                                </>
                              )}
                            </div>
                            <div className="flex gap-1 mt-1 flex-wrap" data-testid={`warning-allergens-${product.id || product.product_barcode}`}>
                              {product.warning_allergens.map(allergen => (
                                <Badge key={allergen} variant="destructive" className="text-xs">
                                  {allergen}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        {product.price && (
                          <Badge variant="secondary" className="text-lg font-semibold">
                            £{product.price.toFixed(2)}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        <div className="mt-12 text-center text-sm text-muted-foreground">
          <p>Powered by <span className="font-semibold text-[#5DBDB3]">Seen Beyond the Label</span></p>
          <p className="mt-1">Always check product labels for the most accurate allergen information.</p>
        </div>
      </main>
    </div>
  );
};

export default StoreScanPage;
